/**
 * useTestDatabaseConnection Hook
 *
 * Custom React Query mutation hook for testing database connection connectivity.
 */

import { useMutation } from "@tanstack/react-query";
import { databaseConnectionService } from "../../../api/database-connection.service";
import { getErrorMessage } from "../../../lib/utils/errors";

interface TestResult {
  success: boolean;
  message: string;
  latencyMs?: number;
}

interface UseTestDatabaseConnectionOptions {
  onSuccess?: (result: TestResult) => void;
  onError?: (error: string) => void;
}

/**
 * Hook for testing database connection connectivity
 * @param tenantId - Tenant ID
 * @param dbConnectionId - Database Connection ID
 * @param options - Success/error callbacks
 */
export const useTestDatabaseConnection = (
  tenantId: string,
  dbConnectionId: string,
  options?: UseTestDatabaseConnectionOptions
) => {
  return useMutation({
    mutationFn: () =>
      databaseConnectionService.testDatabaseConnection(tenantId, dbConnectionId),
    onSuccess: (result) => {
      options?.onSuccess?.(result);
    },
    onError: (error: unknown) => {
      const errorMessage = getErrorMessage(error);
      options?.onError?.(errorMessage);
    },
  });
};
